export type LaborPayBuildingSize = { width: number; length: number };

export function laborPayBuildingSizeFromText(raw: string): LaborPayBuildingSize | null {
  return parseWxL(raw);
}

function parseWxL(raw: string): LaborPayBuildingSize | null {
  const m = raw.trim().replace(/^size:\s*/i, "").match(/(\d+)\s*[x×]\s*(\d+)/i);
  if (!m) return null;
  const width = Number(m[1]);
  const length = Number(m[2]);
  if (!Number.isFinite(width) || !Number.isFinite(length) || width <= 0 || length <= 0) return null;
  return { width, length };
}

export const LABOR_PAYS_BY = [
  { id: "as_ordered", label: "As ordered", short: "as ordered" },
  { id: "each", label: "Each", short: "each" },
  { id: "building_sq_ft", label: "Building square footage", short: "sq ft" },
  { id: "building_length", label: "Building length", short: "length" },
  { id: "building_width", label: "Building width", short: "width" },
  { id: "building_perimeter", label: "Building perimeter", short: "perimeter" },
] as const;

export type LaborPaysBy = (typeof LABOR_PAYS_BY)[number]["id"];

const PAYS_BY_IDS = new Set<string>(LABOR_PAYS_BY.map((row) => row.id));

const PAYS_BY_ALIASES: Record<string, LaborPaysBy> = {
  as_ordered: "as_ordered",
  "as ordered": "as_ordered",
  ordered: "as_ordered",
  line: "as_ordered",
  line_qty: "as_ordered",
  quantity: "as_ordered",
  each: "each",
  once: "each",
  flat: "each",
  building_sq_ft: "building_sq_ft",
  sq_ft: "building_sq_ft",
  sqft: "building_sq_ft",
  "square footage": "building_sq_ft",
  "building square footage": "building_sq_ft",
  building_length: "building_length",
  length: "building_length",
  building_width: "building_width",
  width: "building_width",
  building_perimeter: "building_perimeter",
  perimeter: "building_perimeter",
};

export function parseLaborPaysBy(raw: unknown): LaborPaysBy | null {
  if (typeof raw !== "string") return null;
  const key = raw.trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
  if (!key) return null;
  const spaced = key.replace(/ /g, "_");
  if (PAYS_BY_IDS.has(spaced)) return spaced as LaborPaysBy;
  return PAYS_BY_ALIASES[key] ?? PAYS_BY_ALIASES[spaced] ?? null;
}

export function laborPaysByLabel(id: LaborPaysBy | null | undefined): string {
  if (!id) return "Needs Pays by";
  return LABOR_PAYS_BY.find((row) => row.id === id)?.label ?? id;
}

export function laborPaysByShort(id: LaborPaysBy | null | undefined): string {
  if (!id) return "needs Pays by";
  return LABOR_PAYS_BY.find((row) => row.id === id)?.short ?? id;
}

/** IdeaRoom enumerated $0 / credit: included elsewhere, do not pay this line. Missing price is not zero. */
export function isLaborLineIncluded(price: number | null): boolean {
  return price != null && price <= 0;
}

function parseFeet(raw: string): number | null {
  const m = raw.trim().match(/^(\d+(?:\.\d+)?)\s*(?:'|ft|feet|’)?$/i);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function sizeFromUnknown(raw: unknown): LaborPayBuildingSize | null {
  if (typeof raw !== "string" || !raw.trim()) return null;
  return parseWxL(raw);
}

export function parseLaborPayBuildingSize(
  options: Record<string, unknown> | null | undefined
): LaborPayBuildingSize | null {
  const root = options ?? {};
  const fromSize = sizeFromUnknown(root.size);
  if (fromSize) return fromSize;
  const width = parseFeet(typeof root.width === "string" ? root.width : "");
  const length = parseFeet(typeof root.length === "string" ? root.length : "");
  if (width && length) return { width, length };
  const items = Array.isArray(root.line_items) ? root.line_items : [];
  for (const item of items) {
    if (!item || typeof item !== "object" || Array.isArray(item)) continue;
    const rec = item as Record<string, unknown>;
    const productKey =
      typeof rec.product_key === "string"
        ? rec.product_key
        : typeof rec.productKey === "string"
          ? rec.productKey
          : "";
    if (productKey !== "size") continue;
    const dims =
      sizeFromUnknown(rec.label) ?? sizeFromUnknown(rec.description) ?? sizeFromUnknown(rec.value);
    if (dims) return dims;
  }
  return null;
}

export function laborPayCount(
  paysBy: LaborPaysBy,
  lineQuantity: number,
  building: LaborPayBuildingSize | null
): number | null {
  if (paysBy === "as_ordered") {
    return Number.isFinite(lineQuantity) && lineQuantity > 0 ? lineQuantity : 0;
  }
  if (paysBy === "each") return 1;
  if (!building) return null;
  if (paysBy === "building_sq_ft") return building.width * building.length;
  if (paysBy === "building_length") return building.length;
  if (paysBy === "building_width") return building.width;
  return 2 * (building.width + building.length);
}

export function laborPayAmountUsd(rateUsd: number, count: number): number {
  return Math.round((Number(rateUsd) || 0) * count * 100) / 100;
}
